// Sistema de Permissões por Setores - Frontend

const SECTOR_PERMISSIONS = {
  // 🔴 GERENCIA - Acesso total a todos os setores
  GERENCIA: [
    'all:full', // Acesso total
    'users:full',
    'appointments:full',
    'clients:full',
    'clients:create',
    'inventory:full',
    'financial:full',
    'projects:full',
    'production:full',
    'assembly:full',
    'reports:full',
    'settings:full',
    'settings:technical', // Configurações técnicas
    'settings:payment_conditions', // Condições de pagamento
    // Novas permissões
    'timeclock:full',
    'cortes:full',
    'absences:full',
    'fiscal:full',
    'suppliers:full',
    'sketches:full',
    'holidays:full',
    'schedules:full',
    'trainings:full',
    'payroll:full',
    'profitsharing:full',
    'expenses:full',
    'expensetypes:full',
    'companycashbox:full',
    'payslips:full',
    'taxcontributions:full',
    'workedsaturdays:full',
    'overtimerecords:full',
    'extraadvances:full',
    'advance:full',
    'capacity:full',
    'projecthours:full',
    'settings:clientaccess',
    'surveys:view',
    'surveys:presentation',
    'firstcontact:full',
    'contracts:full'
  ],

  // 🔵 RECEPCAO_COMPRAS_AGENDA - Setor de compras e agenda geral
  RECEPCAO_COMPRAS_AGENDA: [
    'appointments:full', // Todas as agendas
    'appointments:update', // Atualizar agendamentos (confirmar, registrar presença)
    'inventory:full', // Estoque completo
    'financial:expenses',
    'financial:expenses:view',
    'financial:view',
    'production:view', // Acesso ao setor de produção
    'assembly:view', // Acesso ao setor de montagem
    'clients:full', // Dados dos clientes
    'clients:create', // Cadastrar clientes
    'projects:view', // Visualizar projetos
    // Novas permissões
    'timeclock:use',
    'cortes:use',
    'absences:view',
    'suppliers:full',
    'expenses:full',
    'expensetypes:full',
    'companycashbox:full',
    'firstcontact:full',
    'waitinglist:full' // Gerenciar lista de espera
  ],

  // 🟢 RECEPCAO_PROJETOS - Setor de projetos e apresentações
  RECEPCAO_PROJETOS: [
    // Não pode criar levantamentos, apenas visualizar e editar (restrição feita no backend)
    'surveys:view', // Visualizar levantamentos
    'surveys:presentation', // Apresentações de projetos
    'clients:project_data', // Dados do cliente para projetos
    'clients:create', // Cadastrar clientes
    'appointments:full', // Todas as agendas (para agendar da fila de espera)
    'projects:full', // Acesso completo a projetos
    'production:projects', // Aba de projetos na produção
    'assembly:projects', // Aba de projetos na montagem
    'inventory:view', // Visualizar estoque para projetos
    // Novas permissões
    'timeclock:use',
    'cortes:use',
    'sketches:full',
    'firstcontact:full',
    'absences:view',
    'waitinglist:full' // Lista de espera
  ],

  // 🟡 PRODUCAO - Setor de produção
  PRODUCAO: [
    'inventory:view', // Apenas visualização do estoque
    'appointments:deadlines', // Agendas com prazos finais
    'projects:execution', // Aba de projeto para execução
    'clients:production_data', // Dados necessários para produção
    'assembly:view', // Visualizar montagem
    // Novas permissões
    'timeclock:use',
    'absences:view',
    'capacity:view',
    'projecthours:view'
  ],

  // 🟠 MONTAGEM_CLIENTE - Setor de montagem na casa do cliente
  MONTAGEM_CLIENTE: [
    'appointments:delivery', // Agenda de entrega
    'clients:location', // Dados com localização de entrega
    'projects:execution', // Projeto usado para execução
    'inventory:view', // Visualizar estoque para montagem
    'production:view', // Visualizar produção
    // Novas permissões
    'timeclock:use',
    'absences:view'
  ]
};

// Função para verificar se um setor tem uma permissão específica
export const hasPermission = (sector, permission) => {
  const permissions = SECTOR_PERMISSIONS[sector] || [];

  // Se tem acesso total, permite tudo
  if (permissions.includes('all:full')) {
    return true;
  }

  // Verifica permissão específica
  return permissions.includes(permission);
};

// Função para verificar múltiplas permissões (AND)
export const hasAllPermissions = (sector, permissions) => {
  return permissions.every(permission => hasPermission(sector, permission));
};

// Função para verificar se tem pelo menos uma permissão (OR)
export const hasAnyPermission = (sector, permissions) => {
  return permissions.some(permission => hasPermission(sector, permission));
};

// Função para obter todas as permissões de um setor
export const getSectorPermissions = (sector) => {
  return SECTOR_PERMISSIONS[sector] || [];
};

// Mapeamento de setores para cores e ícones
export const SECTOR_CONFIG = {
  ADMIN: {
    name: 'Administrador (Admin)',
    color: 'bg-red-100 text-red-800',
    icon: '👑',
    description: 'Acesso administrativo e técnico total'
  },
  CEO: {
    name: 'Diretoria Executiva (CEO)',
    color: 'bg-purple-100 text-purple-800',
    icon: '🏛️',
    description: 'Supervisão estratégica e controle total'
  },
  GERENCIA: {
    name: 'Gerência',
    color: 'bg-blue-100 text-blue-800',
    icon: '👔',
    description: 'Acesso total ao sistema'
  },
  RECEPCAO_COMPRAS_AGENDA: {
    name: 'Recepção - Compras & Agenda',
    color: 'bg-blue-100 text-blue-800',
    icon: '🛒',
    description: 'Compras, agenda geral e estoque'
  },
  RECEPCAO_PROJETOS: {
    name: 'Recepção - Projetos',
    color: 'bg-green-100 text-green-800',
    icon: '📋',
    description: 'Projetos e apresentações'
  },
  PRODUCAO: {
    name: 'Produção',
    color: 'bg-yellow-100 text-yellow-800',
    icon: '⚙️',
    description: 'Setor de produção'
  },
  MONTAGEM_CLIENTE: {
    name: 'Montagem - Cliente',
    color: 'bg-orange-100 text-orange-800',
    icon: '🔧',
    description: 'Montagem na casa do cliente'
  }
};

// Função para obter configuração de um setor
export const getSectorConfig = (sector) => {
  return SECTOR_CONFIG[sector] || {
    name: 'Setor Desconhecido',
    color: 'bg-gray-100 text-gray-800',
    icon: '❓',
    description: 'Setor não identificado'
  };
};

// Mapeamento de permissões para funcionalidades
export const PERMISSION_MAPPING = {
  // Agendamentos
  'appointments:full': 'Gerenciar todas as agendas',
  'appointments:update': 'Atualizar agendamentos',
  'appointments:aline_own': 'Gerenciar agenda da Aline e própria',
  'appointments:deadlines': 'Visualizar agendas com prazos',
  'appointments:delivery': 'Visualizar agenda de entregas',

  // Estoque
  'inventory:full': 'Gerenciar estoque completo',
  'inventory:view': 'Visualizar estoque',

  // Clientes
  'clients:full': 'Gerenciar dados completos dos clientes',
  'clients:project_data': 'Dados do cliente para projetos',
  'clients:production_data': 'Dados do cliente para produção',
  'clients:location': 'Localização do cliente',
  'clients:create': 'Cadastrar novos clientes',

  // Financeiro
  'financial:full': 'Acesso completo ao financeiro',
  'financial:expenses': 'Registrar saídas de caixa',
  'financial:expenses:view': 'Visualizar despesas registradas',
  'financial:view': 'Visualizar financeiro',

  // Projetos
  'projects:full': 'Gerenciar projetos completos',
  'projects:view': 'Visualizar projetos',
  'projects:execution': 'Acessar projetos para execução',
  'projects:read': 'Ler projetos',
  'projects:create': 'Criar projetos',
  'projects:update': 'Atualizar projetos',
  'projects:delete': 'Deletar projetos',

  // Setores
  'production:full': 'Acesso total à produção',
  'production:view': 'Visualizar produção',
  'production:projects': 'Acessar aba de projetos na produção',
  'assembly:full': 'Acesso total à montagem',
  'assembly:view': 'Visualizar montagem',
  'assembly:projects': 'Acessar aba de projetos na montagem',

  // Levantamentos
  'surveys:full': 'Gerenciar levantamentos',
  'surveys:view': 'Visualizar levantamentos',
  'surveys:presentation': 'Apresentações de projetos',

  // Funcionários e Sistema
  'users:full': 'Gerenciar funcionários',
  'users:manage': 'Gerenciar usuários (salário)',
  'users:read': 'Ler usuários',
  'users:create': 'Criar usuários',
  'reports:full': 'Acessar relatórios',
  'settings:full': 'Configurações do sistema',
  'settings:technical': 'Configurações técnicas (agenda levantamento, preços, pré-produção, etc)',
  'settings:payment_conditions': 'Condições de pagamento',
  'settings:roles': 'Gerenciar roles e permissões',
  'settings:clientaccess': 'Configurar acesso de clientes',
  'all:full': 'Acesso total ao sistema',

  // Ponto e Ausências
  'timeclock:full': 'Gerenciar sistema de ponto',
  'timeclock:use': 'Usar sistema de ponto',
  'absences:full': 'Gerenciar ausências',
  'absences:view': 'Visualizar ausências',

  // Férias
  'vacations:full': 'Gerenciar férias',
  'vacations:view': 'Visualizar férias',

  // Cortes e Esboços
  'cortes:full': 'Gerenciar cortes de PDF',
  'cortes:use': 'Processar cortes de PDF',
  'sketches:full': 'Gerenciar esboços 2D',
  'sketches:view': 'Visualizar esboços 2D',

  // Documentos Fiscais
  'fiscal:full': 'Gerenciar documentos fiscais',
  'fiscal:view': 'Visualizar documentos fiscais',

  // Fornecedores
  'suppliers:full': 'Gerenciar fornecedores',
  'suppliers:view': 'Visualizar fornecedores',

  // Feriados e Horários
  'holidays:full': 'Gerenciar feriados',
  'holidays:view': 'Visualizar feriados',
  'schedules:full': 'Gerenciar horários de trabalho',
  'schedules:view': 'Visualizar horários de trabalho',

  // Treinamentos
  'trainings:full': 'Gerenciar treinamentos',
  'trainings:view': 'Visualizar treinamentos',

  // Despesas
  'expenses:full': 'Gerenciar despesas',
  'expenses:view': 'Visualizar despesas',
  'expensetypes:full': 'Gerenciar tipos de despesas',

  // Caixa da Empresa
  'companycashbox:full': 'Gerenciar caixa da empresa',
  'companycashbox:view': 'Visualizar caixa da empresa',

  // Holerites e folha
  'payslips:full': 'Gerenciar holerites',
  'payslips:view': 'Visualizar holerites',
  'payroll:full': 'Gerenciar folha de pagamento',

  // Contribuições fiscais
  'taxcontributions:full': 'Gerenciar contribuições fiscais',
  'taxcontributions:view': 'Visualizar contribuições fiscais',

  // Horas extras e adiantamentos
  'overtimerecords:full': 'Gerenciar registros de horas extras',
  'overtimerecords:view': 'Visualizar horas extras',
  'extraadvances:full': 'Gerenciar adiantamentos extras',
  'advance:full': 'Gerenciar adiantamentos',

  // Sábados trabalhados
  'workedsaturdays:full': 'Gerenciar sábados trabalhados',

  // Capacidade
  'capacity:full': 'Gerenciar capacidade de produção',
  'capacity:view': 'Visualizar capacidade',

  // Horas de projeto
  'projecthours:full': 'Gerenciar horas de projeto',
  'projecthours:view': 'Visualizar horas de projeto',

  // Participação nos lucros
  'profitsharing:full': 'Gerenciar participação nos lucros',
  'profitsharing:view': 'Visualizar PLR',

  // Primeiro Contato
  'firstcontact:full': 'Gerenciar primeiro contato com clientes',
  'firstcontact:view': 'Visualizar primeiro contato',

  // Contratos de Trabalho
  'contracts:full': 'Gerenciar contratos de trabalho',
  'contracts:view': 'Visualizar contratos de trabalho',

  // Lista de Espera
  'waitinglist:full': 'Gerenciar lista de espera'
};

export default {
  SECTOR_PERMISSIONS,
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
  getSectorPermissions,
  SECTOR_CONFIG,
  getSectorConfig,
  PERMISSION_MAPPING
};